"use client";

import React from "react";
import { ShieldCheck, FileText, Globe, MapPin, CircleCheck, TriangleAlert, BookOpen, ClipboardCheck, Download, Building2, Scale } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const tabCategories = [{
  id: "local",
  label: "Problemy Systemu",
  icon: TriangleAlert
}, {
  id: "international",
  label: "Wydajność i Osady",
  icon: Globe
}, {
  id: "certification",
  label: "Proces Serwisowy",
  icon: ClipboardCheck
}, {
  id: "documentation",
  label: "Korzyści",
  icon: FileText
}];
const localRegulations = [{
  id: "kahramaa",
  title: "Zanieczyszczenia Magnetytowe",
  description: "Czarny szlam (magnetyt) to najczęstsza przyczyna awarii pomp i zatorów w grzejnikach, drastycznie obniżająca sprawność całego układu.",
  status: "Krytyczne",
  points: ["Blokowanie zaworów termostatycznych", "Uszkodzenia wirników pomp obiegowych", "Zimne doły grzejników i niedogrzane pomieszczenia"]
}, {
  id: "qcdd",
  title: "Kamień i Korozja",
  description: "Osady wapienne działają jak izolator na wymiennikach ciepła, zmuszając kocioł do cięższej pracy i zwiększając zużycie paliwa.",
  status: "Ważne",
  points: ["Zwiększone rachunki za gaz lub prąd", "Głośna praca kotła (tzw. czajnikowanie)", "Przyspieszona korozja perforacyjna rur"]
}, {
  id: "limited-heat",
  title: "Ograniczony odbiór ciepła",
  description: "Ograniczony odbiór ciepła prowadzi do przegrzania i przepalenia pieca, co skutkuje wyciekiem i koniecznością zakupu nowego kotła.",
  status: "Krytyczne",
  points: ["Przegrzanie kotła", "Przepalenie elementów grzewczych", "Wycieki i konieczność wymiany pieca"]
}];
const internationalStandards = [{
  id: "iec-61439",
  title: "Sprawność Wymiany Ciepła",
  description: "Nawet 1mm osadu wewnątrz instalacji może obniżyć wydajność oddawania ciepła o blisko 10%, co bezpośrednio przekłada się na koszty ogrzewania."
}, {
  id: "bs-7671",
  title: "Jakość Wody Krążącej",
  description: "Parametry takie jak twardość, zawartość żelaza i manganu decydują o żywotności drogich komponentów kotłowni i armatury regulacyjnej."
}, {
  id: "iso-9001",
  title: "Standardy Konserwacji",
  description: "Regularne płukanie chemiczne i hydrodynamiczne jest zalecane przez większość producentów nowoczesnych kotłów kondensacyjnych."
}];
const inspectionSteps = [{
  step: 1,
  title: "Diagnostyka Systemu",
  description: "Analiza temperatury grzejników kamerą termowizyjną oraz badanie parametrów wody w celu wykrycia ognisk korozji."
}, {
  step: 2,
  title: "Płukanie Hydrodynamiczne",
  description: "Wprowadzenie specjalistycznej pompy czyszczącej, która usuwa zalegający szlam i osady bez demontażu instalacji."
}, {
  step: 3,
  title: "Neutralizacja i Inhibicja",
  description: "Zastosowanie preparatów chemicznych chroniących metalowe elementy przed ponownym osadzaniem się kamienia i rdzy."
}, {
  step: 4,
  title: "Testy i Raport",
  description: "Weryfikacja przepływów, odpowietrzenie układu oraz wystawienie protokołu z wykonanych prac serwisowych."
}];
const requiredDocuments = [{
  name: "Niższe Rachunki",
  type: "Oszczędność",
  required: true
}, {
  name: "Cicha Praca Kotła",
  type: "Komfort",
  required: true
}, {
  name: "Dłuższa Żywotność",
  type: "Ochrona",
  required: true
}, {
  name: "Szybsze Nagrzewanie",
  type: "Wydajność",
  required: true
}, {
  name: "Gwarancja Producenta",
  type: "Bezpieczeństwo",
  required: false
}, {
  name: "Czysta Woda w CO",
  type: "Higiena",
  required: true
}];
const regulatoryBodies = [{
  name: "Magnetyt",
  role: "Czarny osad metaliczny"
}, {
  name: "Kamień Kotłowy",
  role: "Osad wapienny z twardej wody"
}, {
  name: "Rdza",
  role: "Produkty korozji tlenowej"
}, {
  name: "Powietrze",
  role: "Przyczyna korozji i hałasu"
}];
const ongoingCheckpoints = ["Kontrola filtra magnetycznego (co sezon)", "Badanie pH wody w instalacji", "Sprawdzenie ciśnienia w naczyniu przeponowym", "Weryfikacja drożności zaworów", "Monitoring zużycia paliwa", "Uzupełnianie inhibitora korozji", "Odpowietrzanie punktów krytycznych"];
export default function ComplianceStandards() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll();
  const {
    ref: contentRef,
    isVisible: isContentVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="255763" id="heatingcleaningimportance" className="bg-background py-16 md:py-24 overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {}
        <div ref={headerRef} className={`max-w-3xl mb-12 transition-all duration-700 ease-out ${isHeaderVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-lg bg-primary/10 text-primary mb-4">
            <ShieldCheck className="size-4" />
            <span className="text-sm font-medium tracking-wide uppercase font-default">Edukacja i Serwis</span>
          </div>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold font-default text-foreground mb-6 tracking-tight">
            Dlaczego czyszczenie instalacji CO jest tak ważne?
          </h2>
          <p className="text-lg text-muted-foreground leading-relaxed font-default">
            Zaniedbana instalacja grzewcza to nie tylko wyższe rachunki, ale realne ryzyko kosztownych awarii. Dowiedz się, jak osady i korozja wpływają na Twój komfort i portfel.
          </p>
        </div>

        <div ref={contentRef} className={`grid grid-cols-1 lg:grid-cols-3 gap-8 transition-all duration-700 delay-200 ease-out ${isContentVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          
          {}
          <div className="lg:col-span-2">
            <Tabs defaultValue="local" className="w-full">
              <ScrollArea className="w-full pb-2 mb-4">
                <TabsList className="w-full justify-start inline-flex h-auto p-1 bg-muted/50 rounded-lg">
                  {tabCategories.map((tab, i) => {
                  const Icon = tab.icon;
                  return <TabsTrigger key={tab.id} value={tab.id} data-index={i} className="py-2.5 px-4 gap-2 data-[state=active]:bg-background data-[state=active]:shadow-md rounded-md font-default">
                        <Icon className="size-4" />
                        <span className="hidden sm:inline">{tab.label}</span>
                        <span className="sm:hidden">{tab.label.split(' ')[0]}</span>
                      </TabsTrigger>;
                })}
                </TabsList>
              </ScrollArea>

              {}
              <TabsContent value="local" className="space-y-6 mt-2 outline-none">
                <Alert variant="default" className="bg-primary/5 border-primary/20 rounded-lg">
                  <TriangleAlert className="size-4 text-primary" />
                  <AlertTitle className="font-default font-semibold text-foreground">Najczęstsze objawy zanieczyszczeń</AlertTitle>
                  <AlertDescription className="text-muted-foreground font-default">
                    Jeśli Twoje grzejniki są zimne na dole, a kocioł pracuje głośniej niż zwykle, to znak, że instalacja wymaga profesjonalnego płukania.
                  </AlertDescription>
                </Alert>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {localRegulations.map((reg, i) => <Card key={reg.id} data-index={i} className="border-border/50 shadow-md hover:shadow-lg transition-shadow rounded-lg">
                      <CardHeader className="pb-4">
                        <div className="flex justify-between items-start mb-2">
                          <div className="p-2 bg-accent rounded-lg">
                            <Building2 className="size-5 text-primary" />
                          </div>
                          <Badge variant="destructive" className="bg-destructive/10 text-destructive hover:bg-destructive/20 border-transparent font-default">
                            {reg.status}
                          </Badge>
                        </div>
                        <CardTitle className="text-xl font-default text-foreground">{reg.title}</CardTitle>
                        <CardDescription className="text-sm leading-relaxed font-default">
                          {reg.description}
                        </CardDescription>
                      </CardHeader>
                      <CardContent>
                        <ul className="space-y-2">
                          {reg.points.map((point, j) => <li key={j} data-index={j} className="flex items-start gap-2 text-sm text-muted-foreground font-default">
                              {i === 0 || i === 1 || i === 2 ? <TriangleAlert className="size-4 text-primary shrink-0 mt-0.5" /> : <CircleCheck className="size-4 text-primary shrink-0 mt-0.5" />}
                              <span>{point}</span>
                            </li>)}
                        </ul>
                      </CardContent>
                    </Card>)}
                </div>
              </TabsContent>

              {}
              <TabsContent value="international" className="mt-2 outline-none">
                <Card className="border-border/50 shadow-md rounded-lg">
                  <CardHeader>
                    <CardTitle className="font-default text-2xl text-foreground">Wydajność i Standardy</CardTitle>
                    <CardDescription className="font-default">
                      Zrozumienie procesów zachodzących wewnątrz rur pozwala uniknąć niepotrzebnych wydatków na paliwo.
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Accordion type="single" collapsible className="w-full">
                      {internationalStandards.map((std, i) => <AccordionItem key={std.id} value={std.id} data-index={i} className="border-border/50">
                          <AccordionTrigger className="text-base font-semibold font-default hover:text-primary transition-colors">
                            {std.title}
                          </AccordionTrigger>
                          <AccordionContent className="text-muted-foreground leading-relaxed font-default">
                            {std.description}
                          </AccordionContent>
                        </AccordionItem>)}
                    </Accordion>
                  </CardContent>
                </Card>
              </TabsContent>

              {}
              <TabsContent value="certification" className="mt-2 outline-none">
                <div className="space-y-4 relative before:absolute before:inset-0 before:ml-5 before:-translate-x-px md:before:mx-auto md:before:translate-x-0 before:h-full before:w-0.5 before:bg-gradient-to-b before:from-transparent before:via-border before:to-transparent">
                  {inspectionSteps.map((step, i) => <div key={step.step} data-index={i} className="relative flex items-center justify-between md:justify-normal md:odd:flex-row-reverse group is-active">
                      <div className="flex items-center justify-center w-10 h-10 rounded-lg border-4 border-background bg-primary text-primary-foreground font-bold shadow-md shrink-0 md:order-1 md:group-odd:-translate-x-1/2 md:group-even:translate-x-1/2 z-10 font-default">
                        {step.step}
                      </div>
                      <Card className="w-[calc(100%-3rem)] md:w-[calc(50%-2.5rem)] border-border/50 shadow-sm hover:border-primary/30 transition-colors rounded-lg">
                        <CardHeader className="p-4 pb-2">
                          <CardTitle className="text-lg font-default text-foreground">{step.title}</CardTitle>
                        </CardHeader>
                        <CardContent className="p-4 pt-0 text-sm text-muted-foreground font-default">
                          {step.description}
                        </CardContent>
                      </Card>
                    </div>)}
                </div>
              </TabsContent>

              {}
              <TabsContent value="documentation" className="mt-2 outline-none">
                <Card className="border-border/50 shadow-md rounded-lg">
                  <CardHeader>
                    <CardTitle className="font-default text-2xl text-foreground">Główne Korzyści Serwisu</CardTitle>
                    <CardDescription className="font-default">Inwestycja w czyszczenie zwraca się już po pierwszym sezonie grzewczym.</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      {requiredDocuments.map((doc, i) => <div key={i} data-index={i} className="flex items-center justify-between p-3 rounded-lg border border-border/50 bg-muted/30">
                          <div className="flex items-center gap-3">
                            <FileText className="size-4 text-primary" />
                            <div>
                              <p className="text-sm font-medium text-foreground font-default">{doc.name}</p>
                              <p className="text-xs text-muted-foreground font-default">{doc.type}</p>
                            </div>
                          </div>
                          {doc.required ? <Badge variant="default" className="text-[10px] h-5 bg-primary font-default">Gwarantowane</Badge> : <Badge variant="secondary" className="text-[10px] h-5 font-default">Zależne</Badge>}
                        </div>)}
                    </div>
                  </CardContent>
                  <CardFooter className="bg-muted/20 border-t border-border/50 p-4 flex justify-between items-center rounded-b-lg">
                    <span className="text-sm text-muted-foreground font-default">Chcesz poznać szczegóły?</span>
                    <Button variant="outline" size="sm" asChild className="rounded-md border-primary/20 hover:bg-primary/5">
                      <Link to="/ContactPage">Otrzymaj Ofertę</Link>
                    </Button>
                  </CardFooter>
                </Card>
              </TabsContent>

            </Tabs>
          </div>

          {}
          <div className="space-y-6">
            
            {}
            <Card className="border-border/50 shadow-md bg-card rounded-lg">
              <CardHeader className="pb-4">
                <div className="flex items-center gap-2 mb-1">
                  <Scale className="size-5 text-primary" />
                  <CardTitle className="font-default text-lg text-foreground">Wrogowie Instalacji</CardTitle>
                </div>
                <CardDescription className="font-default">Główne czynniki niszczące systemy CO.</CardDescription>
              </CardHeader>
              <CardContent className="p-0">
                <div className="divide-y divide-border/50">
                  {regulatoryBodies.map((body, i) => <div key={i} data-index={i} className="px-6 py-3 flex justify-between items-center hover:bg-accent/50 transition-colors">
                      <div>
                        <span className="font-semibold text-sm text-foreground font-default">{body.name}</span>
                        <p className="text-sm text-muted-foreground block sm:hidden">{body.role}</p>
                      </div>
                      <div className="hidden sm:block">
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <BookOpen className="size-4 text-muted-foreground cursor-help" />
                            </TooltipTrigger>
                            <TooltipContent className="font-default">
                              <p>{body.role}</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </div>
                    </div>)}
                </div>
              </CardContent>
            </Card>

            {}
            <Card className="border-border/50 shadow-md bg-card flex flex-col h-[400px] rounded-lg">
              <CardHeader className="pb-4 shrink-0">
                <CardTitle className="font-default text-lg flex items-center gap-2 text-foreground">
                  <CircleCheck className="size-5 text-primary" />
                  Plan Konserwacji
                </CardTitle>
                <CardDescription className="font-default">Kroki zapewniające ciągłą sprawność ogrzewania.</CardDescription>
              </CardHeader>
              <CardContent className="p-0 flex-1 overflow-hidden">
                <ScrollArea className="h-full px-6 pb-6">
                  <div className="space-y-4 pr-4">
                    {ongoingCheckpoints.map((checkpoint, i) => <div key={i} data-index={i} className="flex items-start gap-3">
                        <div className="mt-0.5 bg-primary/10 p-1 rounded-lg shrink-0">
                          <div className="size-1.5 bg-primary rounded-lg" />
                        </div>
                        <p className="text-sm text-muted-foreground leading-snug font-default">{checkpoint}</p>
                      </div>)}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>

            {}
            <div className="bg-primary/5 rounded-xl p-6 border border-primary/10 text-center shadow-sm">
              <h4 className="font-default font-semibold text-foreground mb-2">Potrzebujesz diagnozy?</h4>
              <p className="text-sm text-muted-foreground mb-4 font-default">Nasi specjaliści z Pszczyny pomogą przywrócić pełną moc Twojej instalacji.</p>
              <Button className="w-full bg-primary hover:bg-primary/90 text-primary-foreground rounded-md font-default" asChild>
                <Link to="/ContactPage">Zamów bezpłatną wycenę</Link>
              </Button>
            </div>

          </div>
        </div>
      </div>
    </section>;
}